import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { vacationCalculation, type VacationCalculation } from '@/api/vacation';
import { refs } from '@/api/refs';
import { esvTipDoc, trimNum } from '@/shared/taxTips';
import InfoQ from '@/shared/components/InfoQ';
import { ProjectField } from '@/components/ProjectSelect';
import DocAccountsBlock from '@/shared/components/DocAccountsBlock';
import { useConfirm } from '@/components/ConfirmProvider';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import TchTable from '@/shared/components/TchTable';

const fmt2 = (v: string | number | undefined) =>
  v == null ? '—' : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function extractError(e: unknown): string {
  const err = e as { response?: { data?: { detail?: string } }; message?: string };
  return err?.response?.data?.detail ?? err?.message ?? 'Помилка';
}

export default function VacationCalculationEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { id } = useParams();
  const [doc, setDoc] = useState<VacationCalculation | null>(null);
  const [avg, setAvg] = useState('');
  // Проект / напрям (упр. облік) — потрапляє у субконто-3 проводок при проведенні.
  const [projectId, setProjectId] = useState('');
  const [docTab, setDocTab] = useState<'calc' | 'accounts'>('calc');
  const [expCode, setExpCode] = useState('92');
  // ч.879: коментар документа — зберігається на blur (як напрям).
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Середню змінили вручну → ховаємо таблицю, лишаємо «Розрахувати» (перерахунок).
  const [avgDirty, setAvgDirty] = useState(false);

  const docQ = useQuery({
    queryKey: ['vacation-calculation', id],
    queryFn: () => vacationCalculation.get(id!),
    enabled: !!id,
  });
  const empQ = useQuery({
    queryKey: ['employees', doc?.organization_id],
    queryFn: () => refs.employees(doc!.organization_id, {}),
    enabled: !!doc?.organization_id,
  });
  const empName = empQ.data?.find((e) => e.id === doc?.employee_id)?.name ?? '—';

  useEffect(() => {
    if (docQ.data) {
      setDoc(docQ.data);
      setAvg(docQ.data.average_daily_wage);
      setProjectId(docQ.data.project_id ?? '');
      setComment(docQ.data.comment ?? '');
      setExpCode(docQ.data.expense_account_code ?? '92');
      setAvgDirty(false);
    }
  }, [docQ.data]);

  const isPosted = doc?.posted ?? false;

  const recalcMut = useMutation({
    mutationFn: () => vacationCalculation.update(id!, avg, undefined, projectId || null),
    onSuccess: (r) => { setDoc(r); setAvg(r.average_daily_wage); setAvgDirty(false); setError(null); },
    onError: (e) => setError(extractError(e)),
  });
  // Зміна проекту зберігається одразу (середня передається поточна — без збиття).
  const projMut = useMutation({
    mutationFn: (pid: string) => vacationCalculation.update(id!, avg, undefined, pid || null),
    onSuccess: (r) => { setDoc(r); setError(null); },
    onError: (e) => setError(extractError(e)),
  });
  // Зміна рахунку витрат документа — зберігається одразу.
  const expMut = useMutation({
    mutationFn: (code: string) =>
      vacationCalculation.update(id!, avg, undefined, undefined, code || null),
    onSuccess: (r) => { setDoc(r); setError(null); },
    onError: (e) => setError(extractError(e)),
  });
  // ч.879: коментар — зберігається одразу (дні/середня — поточні, без збиття).
  const commentMut = useMutation({
    mutationFn: (c: string) => vacationCalculation.update(id!, avg, undefined, undefined, undefined, c),
    onSuccess: (r) => { setDoc(r); setError(null); },
    onError: (e) => setError(extractError(e)),
  });
  const postMut = useMutation({
    mutationFn: () => vacationCalculation.post(id!),
    onSuccess: (r) => {
      setDoc(r); setError(null);
      qc.invalidateQueries({ queryKey: ['vacation-calculations'] });
      qc.invalidateQueries({ queryKey: ['accruals-registry'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpostMut = useMutation({
    mutationFn: () => vacationCalculation.unpost(id!),
    onSuccess: (r) => { setDoc(r); setError(null); },
    onError: (e) => setError(extractError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: () => vacationCalculation.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vacation-calculations'] });
      qc.invalidateQueries({ queryKey: ['accruals-registry'] });
      navigate('/payrolls');
    },
    onError: (e) => setError(extractError(e)),
  });

  if (docQ.isLoading || !doc) return <div className="card">Завантаження…</div>;

  // ч.876 (новий дизайн, Закон #10 + Закон #6): документ завжди збережений
  // (створюється з наказу) — DocumentToolbar без «зберегти» (savable=false);
  // посилання на наказ → «Повʼязані дії»; «розрахувати» — у заголовку блоку.
  // ч.879 (зауваження власника): наказ і так у «Звʼязаних документах»; у
  // «Повʼязаних діях» — «створити виплату» (як у нарахуванні ЗП): період
  // виплати = місяць vacation_start_date (так шукає unpaid-docs).
  const _pd = new Date(doc.vacation_start_date);
  const railActions = [{
    label: 'створити виплату',
    // ч.1347: виплата — зі збереженого нарахування, проведення не чекаємо.
    title: 'Виплата на підставі цього нарахування',
    disabled: false,
    onClick: () => navigate(`/payroll-payments/new?org=${doc.organization_id}&kind=VACATION`
      + `&year=${_pd.getFullYear()}&month=${_pd.getMonth() + 1}&doc=${id}`),
  }];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 0, filled: 0 },
    { id: 'calc', title: 'Розрахунок', required: 0, filled: 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Нарахування (відпускні) № {doc.document_number}</h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          onBackToList={() => navigate('/payrolls')}
          onSave={() => recalcMut.mutate()}
          savable={false}
          isPosted={isPosted}
          onPost={() => postMut.mutate()}
          postPending={postMut.isPending}
          onUnpost={() => unpostMut.mutate()}
          unpostPending={unpostMut.isPending}
          onDelete={() => {
            confirm('Видалити нарахування відпускних? (наказ відпустки лишиться)')
              .then((ok) => { if (ok) deleteMut.mutate(); });
          }}
          deletePending={deleteMut.isPending}
          deleteTitle="Видаляє лише нарахування (наказ відпустки лишається)"
          entriesDoc={{ type: 'doc.vacation_calculations', id: id! }}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>

      {isPosted && (
        <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 6,
          padding: '0.4rem 0.6rem', marginBottom: '0.75rem', color: 'var(--badge-green-fg)' }}>
          ✓ Нарахування проведено. Для редагування — спершу «розпровести».
        </div>
      )}
      {error && <div className="error">{error}</div>}

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={false}
          doc={{ type: 'doc.vacation_calculations', id: id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      {/* Шапка (read-only): працівник + наказ + період. */}
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Працівник</label>
          <input value={empName} readOnly disabled />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Відпустка</label>
          <input value={`${doc.vacation_start_date} — ${doc.vacation_end_date} (${doc.days_count} дн.)`} readOnly disabled />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Розрахунковий період</label>
          <input value={`${doc.calc_period_start} — ${doc.calc_period_end}`} readOnly disabled />
        </div>
        <ProjectField span={2}
            value={projectId}
            organizationId={doc.organization_id}
            onChange={(v) => { setProjectId(v ?? ''); projMut.mutate(v ?? ''); }}
            disabled={isPosted} />
      </div>
      </DocBlock>

      <DocBlock index={2} title="Розрахунок"
        actions={!isPosted ? (
          <button className="primary" disabled={recalcMut.isPending || !avg}
            onClick={() => recalcMut.mutate()}
            title="Перерахунок брутто/ПДФО/ВЗ/ЄСВ за дні наказу × середню (можна перебити середню).">
            розрахувати
          </button>
        ) : undefined}>
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="field" style={{ width: 180 }}>
          <label>Середньоденна</label>
          <input type="number" step="0.01" value={avg} disabled={isPosted}
            onChange={(e) => { setAvg(e.target.value); setAvgDirty(true); }} />
        </div>
      </div>
      {/* ч.1118: з чого взято середню (Порядок №100) — період, оклад, виключені дні. */}
      {doc.calc_note && !avgDirty && (
        <div style={{ margin: '0 0 0.5rem', padding: '0.4rem 0.6rem', borderRadius: 6,
          background: 'var(--bg-soft)', border: '1px solid var(--border)', fontSize: '0.8rem',
          whiteSpace: 'pre-line' }}>
          {doc.calc_note}
        </div>
      )}
      {/* Середню змінили → таблиця прихована, доки не натиснуто «розрахувати». */}
      {avgDirty && !isPosted && (
        <p style={{ color: 'var(--badge-amber-fg)', fontSize: '0.8rem', margin: '0 0 0.5rem' }}>
          Середню змінено. Натисніть «розрахувати» — суми перерахуються.
        </p>
      )}
      {/* Закладки: розрахунок ↔ рахунки обліку (рішення власника 2026-07-07);
          ч.876 — канонічні seg-tabs. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <div className="seg-tabs">
          <button type="button" className={docTab === 'calc' ? 'active' : ''}
            onClick={() => setDocTab('calc')}>Розрахунок</button>
        </div>
        <div className="seg-tabs" style={{ marginLeft: 'auto' }}>
          <button type="button" className={docTab === 'accounts' ? 'active' : ''}
            onClick={() => setDocTab('accounts')}>Рахунки обліку</button>
        </div>
      </div>
      {docTab === 'accounts' && (
        <DocAccountsBlock
          organizationId={doc.organization_id}
          employeeIds={[doc.employee_id]}
          paymentKind="VACATION"
          headerCodes={{ expense: expCode, payroll: '661', pdfo: '6411', wz: '642', esv: '651' }}
          expenseCode={expCode}
          onExpenseChange={(v) => { setExpCode(v); if (v) expMut.mutate(v); }}
          disabled={isPosted}
          nameOf={() => empName}
        />
      )}
      {/* Таблична частина — дизайн як у нарахуванні ЗП. */}
      {docTab === 'calc' && !avgDirty && (
      <div style={{ overflowX: 'auto' }}>
        <TchTable>
          <thead>
            <tr style={{ textAlign: 'right' }}>
              <th style={{ textAlign: 'left' }}>Працівник</th>
              <th style={{ width: 70 }}>Днів</th>
              <th style={{ width: 95 }}>Середня</th>
              <th style={{ width: 95 }}>Брутто</th>
              <th style={{ width: 95 }}>ПДФО</th>
              <th style={{ width: 95 }}>ВЗ</th>
              <th style={{ width: 95 }}>ЄСВ</th>
              <th style={{ width: 100 }}>До виплати</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{empName}</td>
              <td style={{ textAlign: 'right' }}>{doc.days_count}</td>
              <td style={{ textAlign: 'right' }}>{fmt2(doc.average_daily_wage)}</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt2(doc.vacation_gross_amount)}</td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input type="number" className="no-spin pe-cell" readOnly tabIndex={-1}
                    value={trimNum(doc.pdfo_amount)}
                    style={{ textAlign: 'right', width: '100%', background: 'var(--bg-soft)', color: 'var(--text-secondary)' }} />
                  <InfoQ title={`ПДФО — ${trimNum(doc.pdfo_rate)}% від брутто.`} />
                </div>
              </td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input type="number" className="no-spin pe-cell" readOnly tabIndex={-1}
                    value={trimNum(doc.wz_amount)}
                    style={{ textAlign: 'right', width: '100%', background: 'var(--bg-soft)', color: 'var(--text-secondary)' }} />
                  <InfoQ title={`Військовий збір — ${trimNum(doc.wz_rate)}% від брутто.`} />
                </div>
              </td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input type="number" className="no-spin pe-cell" readOnly tabIndex={-1}
                    value={trimNum(doc.esv_amount)}
                    style={{ textAlign: 'right', width: '100%', background: 'var(--bg-soft)', color: 'var(--text-secondary)' }} />
                  <InfoQ title={esvTipDoc(doc.esv_amount, doc.esv_rate)} />
                </div>
              </td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt2(doc.net_amount)}</td>
            </tr>
          </tbody>
        </TchTable>
      </div>
      )}
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: '0.5rem 0 0' }}>
        Виплата відпускних — через «Виплата нарахувань» (борг видно у «Нарахування виплат → Борги»).
      </p>
      </DocBlock>

      <DocBlock index={3} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={comment} disabled={isPosted}
                 onChange={(e) => setComment(e.target.value)}
                 onBlur={() => { if ((doc.comment ?? '') !== comment) commentMut.mutate(comment); }} />
        </div>
        <div>
          <AttachmentsPanel organizationId={doc.organization_id}
            documentType={'doc.vacation_calculations'} documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
    </div>
  );
}
